import { Component, Injector, Input, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Form, FormHandler } from 'src/app/services/forms/form';
import { FormVersion, FormVersionHandler } from '../../../../services/forms/form-version';
import { EbService } from '../../eb.service';
import { FormRestriction } from 'src/app/services/forms/form-restriction';
import { EB2Shutter } from '../../eb-shutter';
import { EB2AcceleratorLocation } from '../../eb-accelerator-location';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { FormService } from 'src/app/controls/form-table/form.service';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { FormVersionService } from 'src/app/services/forms/form-version.service';
import { OtherSystem } from 'src/app/controls/other-system-chips/other-system';
import { CancelDialogComponent } from 'src/app/controls/cancel-dialog/cancel-dialog.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { WfSignatureResource } from 'src/app/components/workflow/workflow';
import moment from 'moment';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { utils } from 'src/app/modules/libs/utils';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { FormType, Roles } from 'src/app/common/enumerations/enumerations';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';

@Component({
  selector: 'eb-main-s1',
  templateUrl: './eb-main-s1.component.html',
  styleUrls: ['./eb-main-s1.component.scss']
})
export class EbMainS1Component extends BaseComponent implements OnChanges {

  private readonly amendmentSubmitCode = 'EB-AMENDMENT-SUBMIT';

  @Input() form?: Form | null;
  @Input() disabled: boolean = true;
  @Input() showAmendment?: boolean;
  @Input() isLogView?: boolean;

  @Output() formCreated = new EventEmitter<Form>();
  @Output() formCancelled = new EventEmitter();

  requesterCtrl = new FormControl();
  descriptionCtrl = new FormControl();
  shuttersCtrl = new FormControl();
  acceleratorCtrl = new FormControl();
  otherSystemCtrl = new FormControl();
  chkBeamlineCtrl = new FormControl();
  chkAcceleratorCtrl = new FormControl();
  startDateCtrl = new FormControl();
  completionDateCtrl = new FormControl();
  restrictionsCtrl = new FormControl();
  bypassCtrl = new FormControl();
  conditionsCtrl = new FormControl();
  systemCtrl = new FormControl();

  formID?: number;
  locationIDs?: (number | undefined)[];

  tmpChkBeamline!: boolean;
  tmpChkAccelerator!: boolean;
  tmpOtherSystems?: OtherSystem[];
  tmpStartDate!: Date;
  tmpCompletionDate!: Date;

  signature?: WfSignatureResource;
  amendmentSignature?: WfSignatureResource;
  canUnsubmit!: boolean;
  usersFiltered?: User[];

  /**
   * Returns active users from the current requester search results.
   */
  get activeUsersFiltered(): User[] {
    // Prevent inactive users from appearing as selectable EB requesters.
    return this.usersFiltered?.filter(user => user.status == 1) ?? [];
  }

  formVersion?: FormVersion;
  cancelAvailable!: boolean;
  disapproveAvailable?: boolean;
  disapproveSignature?: WfSignatureResource;
  resourceTextResetKey = 0;

  constructor(
    protected override injector: Injector,
    public service: EbService,
    private formService: FormService,
    private formVersionService: FormVersionService
  ) {
    super(injector);

  }

  ngOnInit(): void {
    this.buildForm();
  }

  // override async canDeactivate(): Promise<boolean> {
  //   // Add additional logic specific to the child component
  //   return await this.canDeactivate(); // Call the parent method
  // }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  buildForm() {
    this.formGroup = this.formBuilder.group({
      requesterCtrl: this.requesterCtrl,
      descriptionCtrl: this.descriptionCtrl,
      shuttersCtrl: this.shuttersCtrl,
      acceleratorCtrl: this.acceleratorCtrl,
      chkBeamlineCtrl: this.chkBeamlineCtrl,
      chkAcceleratorCtrl: this.chkAcceleratorCtrl,
      otherSystemCtrl: this.otherSystemCtrl,
      startDateCtrl: this.startDateCtrl,
      completionDateCtrl: this.completionDateCtrl,
      restrictionsCtrl: this.restrictionsCtrl,
      bypassCtrl: this.bypassCtrl,
      conditionsCtrl: this.conditionsCtrl,
      systemCtrl: this.systemCtrl
    });
    this.formGroup.markAsPristine();
    this.checkEnableControls();
  }

  checkEnableControls() {
    this.disabled = true;
    this.formGroup?.disable();

    if (this.form) {
      let enable: boolean | null = !this.isLogView;
      if (!this.showAmendment)
        enable = this.form?.editingById == this.currentUser?.id && this.hasPrivilege(this.privilegeEnum.EBNewForm) && ((this.form?.formVersion.statusID ?? 0) == this.formStatusEnum.New || this.form?.formVersion?.statusID == this.formStatusEnum.Draft);
      else
        enable = this.form?.editingById == this.currentUser?.id && this.hasPrivilege(this.privilegeEnum.EBNewForm) && (this.form?.formVersion?.statusID == this.formStatusEnum.AmendmentDrafting && this.hasPrivilege(this.privilegeEnum.EBEditAmendment));

      if (enable) {
        this.disabled = false;
        this.formGroup?.enable();
      }

      this.cancelAvailable = ((this.form?.formVersion?.statusID == this.formStatusEnum.Draft || this.form?.formVersion?.statusID == this.formStatusEnum.New) && this.form?.editingById == this.currentUser?.id) ||
        (this.form?.formVersion?.statusID == this.formStatusEnum.WorkApprovals ||
          (this.form?.formVersion?.statusID == this.formStatusEnum.PendingActivation && !this.form.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 3)?.wfSignatures?.some(s => s.signed))) &&
        this.hasPrivilege(PrivilegeEnum.EBCancel);

      // Workflow responses can contain incomplete role relationships while the graph is being refreshed.
      this.disapproveAvailable = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 2)?.wfSignatures?.some(s => s.roles?.some(r => r?.id == Roles.OIC)) && this.hasRoles([Roles.OIC]);

      if (this.disapproveAvailable)
        this.disapproveSignature = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 2)?.wfSignatures?.find(s => s.roles?.some(r => r?.id == Roles.OIC));
    }
  }

  /**
   * Synchronizes EB fields and amendment actions with the currently selected workflow.
   */
  refresh() {
    if (this.form) {
      this.setOpenDocument(FormType.EBF, this.form.id);
      this.usersFiltered = this.getUsers();
      if (this.showAmendment) {
        this.formVersion = utils.cloneDeep(this.form.formVersion);
      }
      else {
        if (this.form.formVersion.status.amendment)
          this.formVersion = utils.cloneDeep(this.form.formVersion.previousVersion);
        else
          this.formVersion = utils.cloneDeep(this.form.formVersion);
      }
      this.requesterCtrl.setValue(this.formVersion?.requester);
      this.chkBeamlineCtrl.setValue(this.formVersion?.eB2?.beamline);
      this.chkAcceleratorCtrl.setValue(this.formVersion?.eB2?.accelerator);
      this.tmpOtherSystems = this.formVersion?.eB2?.eB2OtherSystems.map(o => {
        const os: OtherSystem = { id: o.id, text: o.text };
        return os;
      });
      this.startDateCtrl.setValue(this.formVersion?.eB2?.permittedStartDate);
      this.completionDateCtrl.setValue(this.formVersion?.eB2?.estimatedCompletion);
      this.getLocationIDs();
      this.signature = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 1)?.wfSignatures?.[0];
      // Amendment submit actions can live in different numbered sections across EB workflow revisions.
      this.amendmentSignature = this.getAmendmentSubmitSignatures().find(s => s.amendmentEnabled);
      this.canUnsubmit = !this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == 2)?.wfSignatures?.some(s => s.signed);
    }
    else {
      this.clearFields();
    }
    this.formGroup?.disable();
    this.formGroup?.markAsPristine();
    this.checkEnableControls();
  }

  clearFields() {
    this.chkBeamlineCtrl.setValue(false);
    this.chkAcceleratorCtrl.setValue(false);
    this.tmpOtherSystems = [];
  }

  selectedReq(e: MatAutocompleteSelectedEvent) {
    const value = e.option.value;
    if (this.formVersion) {
      this.formVersion.requesterID = value.id;
      this.setFormDirty();
    }
  }

  onReqChange(e: any) {
    const value = (e.target.value)?.toLowerCase();
    this.usersFiltered = this.getUsers().filter(u => u.name?.toLowerCase().includes(value));
  }

  descriptionChanged(e: any) {
    if (this.formVersion?.eB2) {
      this.formVersion.eB2.equipmentOrFunctionForBypassAndReason = e;
      this.descriptionCtrl.setValue(e);
      this.setFormDirty();
    }
  }

  relatedDocumentsChanged(e: any) {
    if (this.formVersion?.eB2) {
      this.formVersion.eB2.relatedDocuments = e;
      this.setFormDirty();
    }
  }

  onShutterEdited(e: Resource[]) {
    e.map(r => {
      if (!this.formVersion?.eB2?.eB2Shutters?.find(s => s.resourceID == r.id))
        this.formVersion?.eB2?.eB2Shutters?.push({ id: 0, eB2ID: this.formVersion.eB2?.id, resource: r, resourceID: r.id });
    });
    this.formVersion?.eB2?.eB2Shutters?.map((s, i) => {
      if (!e.find(r => r.id == s.resourceID))
        this.formVersion?.eB2?.eB2Shutters?.splice(i, 1);
    });
    this.setFormDirty();
    this.getLocationIDs();
  }

  onAcceleratorEdited(e: Resource[]) {
    e.map(r => {
      if (!this.formVersion?.eB2?.eB2AcceleratorLocations?.find(s => s.resourceID == r.id))
        this.formVersion?.eB2?.eB2AcceleratorLocations?.push({ id: 0, eB2ID: this.formVersion?.eB2?.id, resource: r, resourceID: r.id });
    });
    this.formVersion?.eB2?.eB2AcceleratorLocations?.map((s, i) => {
      if (!e.find(r => r.id == s.resourceID))
        this.formVersion?.eB2?.eB2AcceleratorLocations?.splice(i, 1);
    });
    this.setFormDirty();
    this.getLocationIDs();
  }

  onOtherSystemsEdited(e: OtherSystem[]) {
    e.map(o => {
      if (!this.formVersion?.eB2?.eB2OtherSystems?.find(x => x.text == o.text))
        this.formVersion?.eB2?.eB2OtherSystems.push({ id: 0, text: o.text, eB2ID: this.formVersion?.eB2.id });
    });
    this.formVersion?.eB2?.eB2OtherSystems.map((x, i) => {
      if (!e.find(o => o.text == x.text))
        this.formVersion?.eB2?.eB2OtherSystems.splice(i, 1);
    });
    this.setFormDirty();
  }

  blCheckboxChanged(e: any) {
    const checked = e.checked;
    this.otherSystemCtrl.setErrors(null);

    if (this.formVersion?.eB2) {
      this.setFormDirty();
      this.formVersion.eB2.beamline = checked;
      if (!checked)
        this.formVersion.eB2.eB2Shutters = [];
    }
    this.getLocationIDs();
  }

  accCheckboxChanged(e: any) {
    const checked = e.checked;
    this.otherSystemCtrl.setErrors(null);

    if (this.formVersion?.eB2) {
      this.setFormDirty();
      this.formVersion.eB2.accelerator = checked;
      if (!checked)
        this.formVersion.eB2.eB2AcceleratorLocations = [];
    }
    this.getLocationIDs();
  }

  getLocationIDs() {
    this.locationIDs = this.formVersion?.eB2?.eB2Shutters.map(s => s.resourceID).concat(this.formVersion.eB2.eB2AcceleratorLocations.map(l => l.resourceID ?? 0));
  }

  getResources(e: EB2Shutter[] | EB2AcceleratorLocation[] | undefined): Resource[] {
    return e?.map(x => x.resource) ?? [];
  }

  restrictionsChanged(e: FormRestriction[]) {
    if (this.formVersion?.eB2)
      this.formVersion.eB2.eB2Restrictions = e;
    this.setFormDirty();
  }

  bypassDetailsChanged(e: any) {
    if (this.formVersion?.eB2)
      this.formVersion.eB2.bypassDetails = e;
    this.bypassCtrl.setValue(e);
    this.setFormDirty();
  }

  conditionsAndlimitationsChanged(e: any) {
    if (this.formVersion?.eB2)
      this.formVersion.eB2.conditionsAndLimitations = e;
    this.conditionsCtrl.setValue(e);
    this.setFormDirty();
  }

  systemAndPrintChanged(e: any) {
    if (this.formVersion?.eB2)
      this.formVersion.eB2.systemAndPrintNumbers = e;
    this.systemCtrl.setValue(e);
    this.setFormDirty();
  }

  startDateChanged(e: any) {
    if (this.formVersion?.eB2)
      this.formVersion.eB2.permittedStartDate = e.value;
    this.setFormDirty();
  }

  completionDateChanged(e: any) {
    if (this.formVersion?.eB2)
      this.formVersion.eB2.estimatedCompletion = e.value;
    this.setFormDirty();
  }

  /**
   * Clears typed-only resource text from Beamline and Accelerator inputs without changing selected chips.
   */
  resetResourceText() {
    this.resourceTextResetKey++;
  }

  /**
   * Persists the EB draft or amendment and clears transient resource search text after a successful save.
   * @param keepLoading Keeps the caller's loading flow active when save is part of submit.
   */
  async save(keepLoading: boolean = false) {
    return new Promise<boolean>(async (resolve, reject) => {
      if (this.form && this.formVersion && this.form?.id <= 0) {
        this.form.formVersion = this.formVersion;
        this.formService.create(FormHandler.toSave(this.form))?.subscribe((data: Form) => {
          this.form = data;
          this.setOpenDocument(FormType.EBF, data.id);
          this.formCreated.emit(data);
          this.setFormDirty(false);
          this.refresh();
          this.resetResourceText();
          if (!keepLoading)
            this.alert.message('DraftSaved', [
              new MessagePlaceholder("{serialNo}", data.sequenceNumber.number)]);
          resolve(true);
        },
          (error) => {
            console.error(error);
            this.alert.defaultError();
            reject(false);
          }
        );
      } else if (this.formVersion) {
        this.formVersionService.update(FormVersionHandler.toSave(this.formVersion, FormType.EBF)).subscribe(() => {
          this.setOpenDocument(FormType.EBF, this.form?.id);
          this.setFormDirty(false);
          this.resetResourceText();
          if (!keepLoading)
            this.showAmendment ? this.alert.message('amendment saved', [
              new MessagePlaceholder("{serialNo}", this.form?.sequenceNumber.number)
            ]) : this.alert.message('DraftUpdated', [
              new MessagePlaceholder("{serialNo}", this.form?.sequenceNumber.number)
            ]);
          resolve(true);
        },
          (error) => {
            console.error(error);
            this.alert.defaultError();
            reject(false);
          }
        );
      }
    });
  }

  /**
   * Saves and submits the active EB after validation, including clearing typed-only resource text.
   */
  async submit() {
    if (this.validateSubmit() && this.form) {
      this.setOpenDocument(FormType.EBF, this.form?.id);
      if (await this.save(true)) {
        await this.formService.setEditing(this.form.id, false).toPromise();
        if (this.signature) {
          this.setFormDirty(false);
          this.service.sign(this.form, this.signature.taskID, this.signature.approveID);
        }
      }
    }
  }

  unsubmit() {
    if (this.signature) {
      this.service.sign(this.form, this.signature.taskID, this.signature.unapproveID)
    }
  }

  disapprove() {
    if (this.disapproveSignature) {
      this.service.sign(this.form, this.disapproveSignature.taskID, this.disapproveSignature.disapproveID)
    }
  }

  validateSubmit(): boolean {
    let result = true;
    if (!this.formVersion?.requesterID) {
      this.alert.message('EB_No_Requester_Was_Specified');
      ;
      this.requesterCtrl.setErrors({ required: true });
      result = false;
    }

    if (
      !this.formVersion?.eB2?.equipmentOrFunctionForBypassAndReason ||
      this.formVersion.eB2.equipmentOrFunctionForBypassAndReason.trim() === ""
    ) {
      this.alert.message('EB_No_Equipment_Or_Function_For_Bypass_And_Reason_Was_Specified');
      ;
      this.descriptionCtrl.setErrors({ required: true });
      result = false;
    }

    if (this.formVersion?.eB2?.beamline && !this.formVersion.eB2.eB2Shutters.length) {
      this.shuttersCtrl.setErrors({ required: true });
      this.alert.message('EB_Please_Check_On_At_Least_One_System_Affected');
      ;
      result = false;
    }

    if (this.formVersion?.eB2?.accelerator && !this.formVersion.eB2.eB2AcceleratorLocations) {
      this.acceleratorCtrl.setErrors({ required: true });
      this.alert.message('EB_Please_Check_On_At_Least_One_System_Affected');
      ;
      result = false;
    }

    if (!this.formVersion?.eB2?.eB2OtherSystems.length &&
      !this.formVersion?.eB2?.eB2Shutters.length &&
      !this.formVersion?.eB2?.eB2AcceleratorLocations.length
    ) {
      this.alert.message('EB_Please_Check_On_At_Least_One_System_Affected');
      ;
      this.otherSystemCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.eB2?.eB2Restrictions.length) {
      this.alert.message('EB_No_Restrictions_Were_Added');
      ;
      this.restrictionsCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.eB2?.bypassDetails || this.formVersion.eB2.bypassDetails.trim() === "") {
      this.alert.message('EB_No_Bypass_Location_And_Details_Were_Specified');
      ;
      this.bypassCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.eB2?.conditionsAndLimitations || this.formVersion.eB2.conditionsAndLimitations.trim() === "") {
      this.alert.message('EB_No_Conditions_And_Limitations_Were_Specified');
      ;
      this.conditionsCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.eB2?.systemAndPrintNumbers || this.formVersion.eB2.systemAndPrintNumbers.trim() === "") {
      this.alert.message('EB_No_System_And_Print_Numbers_Were_Specified');
      ;
      this.systemCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.eB2?.permittedStartDate) {
      this.alert.message('EB_No_Permitted_Start_Date_Was_Specified');
      ;
      this.startDateCtrl.setErrors({ required: true });
      result = false;
    }

    if (!this.formVersion?.eB2?.estimatedCompletion) {
      this.alert.message('EB_No_Estimated_Completion_Date_Was_Specified');
      ;
      this.completionDateCtrl.setErrors({ required: true });
      result = false;
    }

    if (this.formVersion?.eB2?.estimatedCompletion && this.formVersion.eB2.permittedStartDate) {
      if (moment(this.formVersion.eB2.estimatedCompletion).isBefore(this.formVersion.eB2.permittedStartDate)
      ) {
        this.alert.message('EB_The_Estimated_Completion_Date_Is_Less_Than_The_Permitted_Start_Date');
        ;
        this.completionDateCtrl.setErrors({ invalidDate: true })
        result = false;
      }
    }

    if (!result)
      this.formGroup?.markAllAsTouched();

    return result;
  }

  /**
   * Saves and submits an EB amendment after validation, including clearing typed-only resource text.
   */
  submitAmendment() {
    if (this.validateSubmit() && this.form && this.formVersion) {
      this.setOpenDocument(FormType.EBF, this.form?.id);
      this.formVersionService.update(FormVersionHandler.toSave(this.formVersion, FormType.EBF)).subscribe((data: FormVersion) => {
        if (this.form && data) {
          this.formVersion = data;
          this.setFormDirty(false);
          this.resetResourceText();
          // Resolve the configured amendment action without depending on workflow section numbering.
          const signature = this.getAmendmentSubmitSignatures().find(s => s.approveEnabled && !s.signed && !!s.approveID);
          if (signature)
            this.service.sign(this.form, signature.taskID, signature.approveID)
        }
      },
        (error) => {
          console.error(error);
          this.alert.defaultError();
        }
      );
    }
  }

  /**
   * Returns a submitted EB amendment to drafting through its coded workflow task.
   */
  unsubmitAmendment() {
    const signature = this.getAmendmentSubmitSignatures().find(s => s.unapproveEnabled && !!s.unapproveID);
    if (signature) {
      this.service.sign(this.form, signature.taskID, signature.unapproveID);
    }
  }

  // async cancelNew() {
  //   if (this.form?.id) {
  //     const amendment = this.formVersion?.status.amendment;
  //     // const confirm = this.dialog.open(CancelDialogComponent, {
  //     //   width: "600px",
  //     //   data: {
  //     //     amendment: this.formVersion?.status.amendment,
  //     //   },
  //     // });
  //     const confirm = this.dialog.open(YesNoDialogComponent, {
  //       width: "600px",
  //       data: {
  //         message: this.getMessage('CancelFormDialogTitle')?.description
  //       },
  //     });

  //     confirm.afterClosed().subscribe((data) => {
  //       if (data) {
  //         if (this.form?.sequenceNumber.number) {
  //           this.formService.delete(this.form.sequenceNumber.number).subscribe(data => {
  //             this.alert.message('Form_SerialNo_deleted', [
  //               new MessagePlaceholder("{serialNo}", this.form?.sequenceNumber?.number),
  //             ]);
  //             this.form = null;
  //             this.formVersion = undefined;
  //             this.setFormDirty(false);
  //             this.formCreated.emit(undefined);
  //           });
  //         }
  //       }
  //     });
  //   }
  //   else if (await this.canDeactivate()) {
  //     this.formService.deleteSerialNumbers(FormType.EBF, this.currentUser?.id as number).toPromise();
  //     this.form = null;
  //     this.formVersion = undefined;
  //     this.setFormDirty(false);
  //     this.formCreated.emit(undefined);
  //   }
  // }

  /**
   * Cancels the current EB or amendment through the applicable coded workflow action.
   */
  async cancel() {
    if (this.form?.formVersion?.statusID == this.formStatusEnum.New) {
      try {
        await this.save();
      } catch (err) {
        console.log("Error saving form before canceling:", err);
        return;
      }
    }
    const amendment = this.form?.formVersion.status.amendment;
    const confirm = this.dialog.open(CancelDialogComponent, {
      width: "600px",
      data: {
        amendment: this.form?.formVersion.status.amendment,
      },
    });

    const signature = amendment ? this.getAmendmentSubmitSignatures().find(s => s.cancelEnabled && !!s.cancelID) : this.signature;

    confirm.afterClosed().subscribe(data => {
      if (data?.accept) {
        this.service.sign(this.form, signature?.taskID, signature?.cancelID, data.text).then(() => {
          if (!amendment) {
            this.alert.message('CancelSuccess', [new MessagePlaceholder("{reason}", data.text),]);
          }
          // if (this.form?.id) this.formService.setEditing(this.form?.id, false);
          this.form = null;
          this.setFormDirty(false);
          this.refresh();
          this.formCancelled.emit();
        }).catch(() => { this.alert.defaultError(); });
      }
    });
  }

  /**
   * Determines whether the current user may withdraw the pending EB amendment.
   * @returns True when an enabled workflow unapprove action and amendment-request permission are available.
   */
  canUnsubmitAmendment() {
    const amendmentUnapproveSignature = this.getAmendmentSubmitSignatures().some(s => s.unapproveEnabled && !!s.unapproveID);
    return [this.formStatusEnum.AmendmentPending, this.formStatusEnum.AmendmentReviewApprovals].includes(this.form?.formVersion?.statusID ?? 0) &&
      amendmentUnapproveSignature &&
      this.hasPrivilege(PrivilegeEnum.EBRequestAmendment);
  }

  /**
   * Returns EB amendment-submit signatures from every workflow section using their stable task code.
   * Historical amendment instances are excluded by matching the active workflow step.
   * @returns Matching signatures across legacy and current EB workflow layouts.
   */
  private getAmendmentSubmitSignatures(): WfSignatureResource[] {
    const signatures = this.form?.wfTable?.wfTableLocal?.wfSectionLocals
      .flatMap(section => section.wfSignatures ?? [])
      ?? [];
    const activeAmendmentID = this.getActiveAmendmentID();
    const currentSignatures = signatures.filter(signature => (signature.wfAmendmentID ?? 0) == activeAmendmentID);
    const codedSignatures = currentSignatures.filter(signature => signature.code == this.amendmentSubmitCode);

    // Older workflow instances may predate the amendment-submit code migration; retain their
    // configured amendment action while using the stable code whenever it is present.
    return codedSignatures.length > 0
      ? codedSignatures
      : currentSignatures.filter(signature => signature.amendmentEnabled);
  }

  /**
   * Resolves the amendment identifier attached to the active workflow step.
   * @returns The active amendment identifier, or zero for the base workflow.
   */
  private getActiveAmendmentID(): number {
    const activeStep = this.form?.wfTable?.wfTableLocal?.wfSectionLocals
      .flatMap(section => section.wfStepLocals ?? [])
      .flatMap(step => step.wfSteps ?? [])
      .find(step => step.active);
    return activeStep?.wfAmmendmentID ?? 0;
  }

  /**
   * Checks whether the coded amendment-submit action has already been signed.
   * @returns True when any workflow section contains a signed amendment-submit task.
   */
  hasSignedAmendmentSubmit(): boolean {
    return this.getAmendmentSubmitSignatures().some(signature => signature.signed);
  }

  diffRelatedDocuments(): boolean | undefined {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      (this.formVersion?.eB2?.relatedDocuments ?? []).slice().sort((a, b) => utils.sort(a.relatedDocumentId, b.relatedDocumentId, true)).map((l) => {
        return {
          id: l.relatedDocumentId
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify((this.formVersion?.previousVersion?.eB2?.relatedDocuments ?? []).slice().sort((a, b) => utils.sort(a.relatedDocumentId, b.relatedDocumentId, true)).map((l) => {
      return {
        id: l.relatedDocumentId
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }

  diffRelatedLinks() {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      this.formVersion?.eB2?.eB2RelatedLinks?.slice().sort((a, b) => utils.sort(a.id, b.id, true)).map((l) => {
        return {
          text: l.text,
          url: l.url
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.eB2?.eB2RelatedLinks?.slice().sort((a, b) => utils.sort(a.id, b.id, true)).map((l) => {
      return {
        text: l.text,
        url: l.url
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }

  diffRestrictions() {
    if (!this.showAmendment) return;
    // Sort copies only: these live arrays drive the restriction table's display
    // order, and an in-place sort here reshuffles the visible list.
    const current = utils.JSONstringify(
      this.formVersion?.eB2?.eB2Restrictions?.slice().sort((a, b) => utils.sort(a.restrictionID, b.restrictionID, true)).map((l) => {
        return {
          restrictionID: l.restrictionID,
          text: l.text,
        } as FormRestriction;
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.eB2?.eB2Restrictions?.slice().sort((a, b) => utils.sort(a.restrictionID, b.restrictionID, true)).map((l) => {
      return {
        restrictionID: l.restrictionID,
        text: l.text,
      } as FormRestriction;
    }))?.replace(/\s/g, "");
    this.formVersion?.eB2?.eB2Restrictions.map(r => {
      r.hilite = this.formVersion?.previousVersion?.eB2?.eB2Restrictions.find(p => p.restrictionID ? p.restrictionID == r.restrictionID : p.text == r.text) == null;
    });
    return current != original;
  }

  diffShutters() {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      this.formVersion?.eB2?.eB2Shutters?.slice().sort((a, b) => utils.sort(a.resourceID, b.resourceID, true)).map((l) => {
        return {
          id: l.resourceID
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.eB2?.eB2Shutters?.slice().sort((a, b) => utils.sort(a.resourceID, b.resourceID, true)).map((l) => {
      return {
        id: l.resourceID
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }

  diffAccelerators() {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      this.formVersion?.eB2?.eB2AcceleratorLocations?.slice().sort((a, b) => utils.sort(a.resourceID, b.resourceID, true)).map((l) => {
        return {
          id: l.resourceID
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.eB2?.eB2AcceleratorLocations?.slice().sort((a, b) => utils.sort(a.resourceID, b.resourceID, true)).map((l) => {
      return {
        id: l.resourceID
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }

  diffOtherSystems() {
    if (!this.showAmendment) return;
    const current = utils.JSONstringify(
      this.formVersion?.eB2?.eB2OtherSystems?.slice().sort((a, b) => utils.sort(a.text, b.text, true)).map((l) => {
        return {
          text: l.text,
        };
      }))?.replace(/\s/g, "");
    const original = utils.JSONstringify(this.formVersion?.previousVersion?.eB2?.eB2OtherSystems?.slice().sort((a, b) => utils.sort(a.text, b.text, true)).map((l) => {
      return {
        text: l.text,
      };
    }))?.replace(/\s/g, "");
    return current != original;
  }

}
